import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope,
  FaClock,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaWhatsapp
} from 'react-icons/fa';
import { contactAPI } from '../utils/api';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    serviceInterested: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    document.title = 'Contact Us | Infrastructure Company';
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await contactAPI.submit(formData);
      setSuccess(true);
      setFormData({ 
        name: '', 
        email: '', 
        phone: '', 
        subject: '', 
        serviceInterested: '',
        message: '' 
      });
      setValidated(false);
      
      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt />,
      title: 'Visit Us',
      content: '123, Infrastructure Street, Mumbai, Maharashtra 400001, India'
    },
    {
      icon: <FaPhone />,
      title: 'Call Us',
      content: '+91 98765 43210',
      link: 'tel:+919876543210'
    },
    {
      icon: <FaEnvelope />,
      title: 'Email Us',
      content: 'info@yourcompany.com',
      link: 'mailto:info@yourcompany.com'
    },
    {
      icon: <FaClock />,
      title: 'Working Hours',
      content: 'Mon - Sat: 9:00 AM - 6:00 PM'
    }
  ];

  const services = [
    'Underground Cable Wiring',
    'Electricity Meters',
    'Road Construction',
    'Road Building',
    'Underground Works',
    'General Inquiry',
    'Other'
  ];

  return (
    <div className="contact-page">
      {/* Page Header */}
      <section className="page-header">
        <Container>
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-3">Contact Us</h1>
            <p className="lead">
              Get in Touch with Our Infrastructure Experts
            </p>
          </div>
        </Container>
      </section>

      {/* Contact Info Cards */}
      <section className="section">
        <Container>
          <Row className="g-4 mb-5">
            {contactInfo.map((info, index) => (
              <Col key={index} md={6} lg={3}>
                <Card className="contact-info-card h-100 text-center">
                  <Card.Body className="p-4">
                    <div className="info-icon mb-3">{info.icon}</div>
                    <h5 className="mb-3">{info.title}</h5>
                    {info.link ? (
                      <a href={info.link} className="info-content">
                        {info.content}
                      </a>
                    ) : (
                      <p className="info-content mb-0">{info.content}</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            {/* Contact Form */}
            <Col lg={8}>
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(false)} className="mb-4">
                  <Alert.Heading>✅ Message Sent Successfully!</Alert.Heading>
                  <p>
                    Thank you for contacting us. Our team will get back to you within 24-48 hours.
                  </p>
                </Alert>
              )}

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
                  <strong>Error:</strong> {error}
                </Alert>
              )}

              <Card className="contact-form-card">
                <Card.Body className="p-4">
                  <h3 className="mb-4">Send Us a Message</h3>
                  
                  <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Your Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter your name.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Email Address *</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter a valid email.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Phone Number *</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="10-digit mobile number"
                            pattern="[6-9][0-9]{9}"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter a valid 10-digit Indian mobile number.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Service Interested</Form.Label>
                          <Form.Select
                            name="serviceInterested"
                            value={formData.serviceInterested}
                            onChange={handleChange}
                          >
                            <option value="">Select a service</option>
                            {services.map((service, index) => (
                              <option key={index} value={service}>
                                {service}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Subject *</Form.Label>
                          <Form.Control
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="Brief subject of your inquiry"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter a subject.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Your Message *</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={5}
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tell us about your project or inquiry..."
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter your message.
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Button 
                          type="submit" 
                          variant="primary" 
                          size="lg" 
                          className="w-100"
                          disabled={loading}
                        >
                          {loading ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* Sidebar */}
            <Col lg={4}>
              {/* Office Hours */}
              <Card className="mb-4">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Office Hours</h5>
                  <div className="office-hours">
                    <div className="hours-item">
                      <strong>Monday - Friday</strong>
                      <span>9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="hours-item">
                      <strong>Saturday</strong>
                      <span>9:00 AM - 2:00 PM</span>
                    </div>
                    <div className="hours-item">
                      <strong>Sunday</strong>
                      <span>Closed</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Social Media */}
              <Card className="mb-4">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Follow Us</h5>
                  <div className="social-links-vertical">
                    <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link-item">
                      <FaFacebookF /> Facebook
                    </a>
                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-link-item">
                      <FaTwitter /> Twitter
                    </a>
                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-link-item">
                      <FaLinkedinIn /> LinkedIn
                    </a>
                    <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link-item">
                      <FaInstagram /> Instagram
                    </a>
                    <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="social-link-item whatsapp">
                      <FaWhatsapp /> WhatsApp
                    </a>
                  </div>
                </Card.Body>
              </Card>

              {/* Quick Contact */}
              <Card className="quick-contact-card">
                <Card.Body className="p-4 text-center">
                  <h5 className="mb-3">Need Immediate Help?</h5>
                  <p className="text-muted mb-3">
                    Call us directly for urgent inquiries
                  </p>
                  <Button 
                    href="tel:+919876543210" 
                    variant="primary" 
                    size="lg" 
                    className="w-100"
                  >
                    <FaPhone className="me-2" />
                    Call Now
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <Container fluid className="px-0">
          <div className="map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609823277!2d72.74109995709658!3d19.08219783958221!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c6306644edc1%3A0x5da4ed8f8d648c69!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1234567890123"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Company Location"
            ></iframe>
          </div>
        </Container>
      </section>

      <div className="indian-accent"></div>
    </div>
  );
};

export default Contact;